import { cn } from "../../lib/utils";

function Skeleton({ className, ...props }) {
  return (
    <div
      className={cn(
        "animate-pulse rounded-md bg-gray-200 dark:bg-[#21262d]",
        className
      )}
      {...props}
    />
  );
}

function SkeletonCard({ darkMode = true, className }) {
  return (
    <div className={cn(
      "rounded-2xl p-6 border",
      darkMode ? "bg-[#161b22] border-[#30363d]" : "bg-white border-gray-100",
      className
    )}>
      <div className="flex items-center justify-between mb-4">
        <Skeleton className={cn("h-4 w-24", darkMode && "bg-[#21262d]")} />
        <Skeleton className={cn("h-8 w-8 rounded-lg", darkMode && "bg-[#21262d]")} />
      </div>
      <Skeleton className={cn("h-10 w-16 mb-2", darkMode && "bg-[#21262d]")} />
      <Skeleton className={cn("h-3 w-20", darkMode && "bg-[#21262d]")} />
    </div>
  );
}

function SkeletonList({ count = 5, darkMode = true, className }) {
  return (
    <div className={cn("space-y-4", className)}>
      {Array.from({ length: count }).map((_, i) => (
        <div 
          key={i} 
          className={cn(
            "rounded-xl p-5 border",
            darkMode ? "bg-[#161b22] border-[#30363d]" : "bg-white border-gray-100"
          )}
          style={{ animationDelay: `${i * 100}ms` }}
        >
          <div className="flex items-start gap-4">
            <Skeleton className={cn("h-12 w-12 rounded-lg flex-shrink-0", darkMode && "bg-[#21262d]")} />
            <div className="flex-1 space-y-2">
              <Skeleton className={cn("h-4 w-3/4", darkMode && "bg-[#21262d]")} />
              <Skeleton className={cn("h-3 w-1/2", darkMode && "bg-[#21262d]")} />
            </div>
            <Skeleton className={cn("h-6 w-16 rounded-full", darkMode && "bg-[#21262d]")} />
          </div>
        </div>
      ))}
    </div>
  );
}

function SkeletonTable({ rows = 5, cols = 4, darkMode = true }) {
  return (
    <div className={cn(
      "rounded-xl border overflow-hidden",
      darkMode ? "bg-[#161b22] border-[#30363d]" : "bg-white border-gray-100"
    )}>
      {/* Header */}
      <div className={cn(
        "flex items-center gap-4 p-4 border-b",
        darkMode ? "border-[#21262d] bg-[#0d1117]" : "border-gray-100 bg-gray-50"
      )}>
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton 
            key={i} 
            className={cn("h-4 flex-1", darkMode && "bg-[#21262d]")} 
          />
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <div 
          key={rowIdx}
          className={cn(
            "flex items-center gap-4 p-4 border-b last:border-b-0",
            darkMode ? "border-[#21262d]" : "border-gray-100"
          )}
        >
          {Array.from({ length: cols }).map((_, colIdx) => (
            <Skeleton 
              key={colIdx} 
              className={cn(
                "h-4 flex-1",
                colIdx === 0 && "w-1/4 flex-none",
                darkMode && "bg-[#21262d]"
              )} 
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function SkeletonChart({ darkMode = true, height = "h-64" }) {
  return (
    <div className={cn(
      "rounded-xl border overflow-hidden",
      darkMode ? "bg-[#161b22] border-[#30363d]" : "bg-white border-gray-100"
    )}>
      <div className={cn("p-4 border-b", darkMode ? "border-[#21262d]" : "border-gray-100")}>
        <Skeleton className={cn("h-5 w-32", darkMode && "bg-[#21262d]")} />
      </div>
      <div className={cn("p-4", height)}>
        <div className="flex items-end justify-around h-full gap-2">
          {Array.from({ length: 7 }).map((_, i) => (
            <Skeleton 
              key={i} 
              className={cn("w-8 rounded-t-lg", darkMode && "bg-[#21262d]")} 
              style={{ height: `${30 + Math.random() * 60}%` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export { Skeleton, SkeletonCard, SkeletonList, SkeletonTable, SkeletonChart };
